import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'moment-timezone';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {DataGridPro, GridActionsCellItem} from '@mui/x-data-grid-pro';
import {faPlay, faAlignLeft, faEllipsisHAlt, faUser} from '@fortawesome/pro-light-svg-icons';
import {faPlus} from '@fortawesome/pro-regular-svg-icons';

import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import LessonMenu from './LessonMenu';
import InstructionsPreviewDialog from './InstructionsPreviewDialog';
import CreateLessonDialog from './CreateLessonDialog';
import CopyLessonDialog from './CopyLessonDialog';
import VisibilityChip from './VisibilityChip';
import DataGridQuickSearchToolbar from './DataGridQuickSearchToolbar';
import {fetchLessons, selectLessons, selectIsLoading as selectIsBuilderLoading} from '../../redux/slices/builder';
import {
  selectUserGroup,
  selectUserGroups,
  fetchUserGroup,
  fetchUsers,
  selectUser,
  selectUsers,
  selectIsLoading as selectIsUserLoading,
} from '../../redux/slices/user';
import {FEATURES} from '../../constants';
import ImageNoData from '../images/no-data.svg';

import styles from '../css/Lessons.module.css';
import baseStyles from '../../css/Base.module.css';

export default function Lessons() {
  const dispatch = useDispatch();

  const [isMenuOpen, toggleMenu] = useState(false);
  const [activeLesson, setActiveLesson] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLessonDialogOpen, toggleLessonDialogOpen] = useState(false);
  const [isCopyLessonDialogOpen, toggleCopyLessonDialog] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(null);
  const [lessonsClone, setLessonsClone] = useState([]); // TODO: Refactor this away
  const [searchText, setSearchText] = useState('');
  const [dataGridToolbarFilterButtonEl, setDataGridToolbarFilterButtonEl] = useState(false);
  const [isInstructionsPreviewDialogOpen, setIsInstructionsPreviewDialogOpen] = useState(false);

  const userGroup = useSelector(selectUserGroup);
  const userGroups = useSelector(selectUserGroups);
  const user = useSelector(selectUser);
  const users = useSelector(selectUsers);
  const allLessons = useSelector(selectLessons);
  const isBuilderLoading = useSelector(selectIsBuilderLoading);
  const isUserLoading = useSelector(selectIsUserLoading);

  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);
  const isLoading = isBuilderLoading || isUserLoading;

  const myLessonsFilter = (lesson) => lesson.userID === user._id;
  const sharedWithMeFilter = (lesson) => lesson.userID !== user._id && lesson.visibility === 'USER_GROUP';
  const flamingoFilter = (lesson) => lesson.visibility === 'PUBLIC';
  let filterFn = myLessonsFilter;
  if (activeTabIndex === 1) filterFn = sharedWithMeFilter;
  else if (activeTabIndex === 2) filterFn = flamingoFilter;
  let lessons = allLessons.filter(filterFn).map((lesson) => ({id: lesson._id, ...lesson})); // DataGrid requires unique id property

  // Quick search toolbar BEGIN
  function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = lessons.filter((row) => {
      return Object.keys(row).some((field) => {
        let searchValue;
        if (field === 'tags') searchValue = row[field].map((tag) => tag.name).join(',');
        else if (field === 'visibility') searchValue = row[field] === 'USER_GROUP' ? 'Everyone' : row[field];
        else searchValue = row[field]?.toString() ?? '';

        return searchRegex.test(searchValue);
      });
    });
    setLessonsClone(filteredRows || []);
  };
  // Quick search toolbar END

  useEffect(() => {
    if (isContentProducerGroup) setActiveTabIndex(2);
    else setActiveTabIndex(0);
  }, [isContentProducerGroup]);

  useEffect(() => {
    setLessonsClone([]);
    if (activeTabIndex === 0) dispatch(fetchLessons({userID: user._id}));
    else if (activeTabIndex === 1) dispatch(fetchLessons({visibility: 'USER_GROUP'}));
    else if (activeTabIndex === 2) {
      const userGroupIDs = isContentProducerGroup ? [userGroup._id] : Object.keys(userGroup.tier.content);
      userGroupIDs.forEach((userGroupID) => dispatch(fetchLessons({userGroupID, visibility: 'PUBLIC'})));
    }
  }, [dispatch, activeTabIndex, user._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useDeepCompareEffect(() => {
    fetchMissingLogoEntities();
  }, [lessons]);

  useDeepCompareEffect(() => {
    if (hasAllLogoEntities()) {
      setLessonsClone(lessons);
    }
  }, [lessons, users, userGroups, isBuilderLoading]);

  function hasAllLogoEntities() {
    if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
      const userIDs = Array.from(new Set(lessons.map((lesson) => lesson.userID)));
      return userIDs.every((userID) => Object.keys(users).includes(userID));
    } else if (activeTabIndex === 2) {
      const userGroupIDs = Object.keys(userGroup.tier.content);
      return userGroupIDs.every((userGroupID) => Object.keys(userGroups).includes(userGroupID));
    }
  }

  function fetchMissingLogoEntities() {
    if (lessons.length > 0) {
      if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
        const userIDs = Array.from(new Set(lessons.map((lesson) => lesson.userID))).filter(
          (userID) => !Object.keys(users).includes(userID),
        );
        if (userIDs.length > 0) dispatch(fetchUsers(userIDs));
      } else if (activeTabIndex === 2) {
        const userGroupIDs = Object.keys(userGroup.tier.content);
        userGroupIDs.forEach((userGroupID) => dispatch(fetchUserGroup(userGroupID)));
      }
    }
  }

  function handleClickMenu(event, lesson) {
    setActiveLesson(lesson);
    setAnchorEl(event.currentTarget);
    toggleMenu(true);
    event.stopPropagation();
  }

  function handleClickPreviewLesson(event, lesson) {
    const {_id: lessonID} = lesson;
    window.open(`/lesson/${lessonID}`);
  }

  function handleClickPreviewInstructions(event, lesson) {
    setActiveLesson(lesson);
    setIsInstructionsPreviewDialogOpen(true);
    event.stopPropagation();
  }

  function handleClickNewLesson() {
    setActiveLesson(null);
    toggleLessonDialogOpen(true);
  }

  function handleChangeTab(event, newTabIndex) {
    setActiveTabIndex(newTabIndex);
  }

  return (
    <>
      <CreateLessonDialog isOpen={isLessonDialogOpen} handleClose={() => toggleLessonDialogOpen(false)} />
      <InstructionsPreviewDialog
        isOpen={isInstructionsPreviewDialogOpen}
        setIsOpen={setIsInstructionsPreviewDialogOpen}
        instructionsHTML={activeLesson?.instructions?.html}
      />

      <PrimaryAppBar />
      <SecondaryAppBar title="Lessons">
        <IconButton
          onClick={handleClickNewLesson}
          className="bg-success"
          size="large"
          sx={{'&:hover': {backgroundColor: 'rgba(66, 184, 94, 0.8)'}}}
        >
          <FontAwesomeIcon icon={faPlus} size="xs" className="clr-gray-200" />
        </IconButton>
      </SecondaryAppBar>

      {Number.isInteger(activeTabIndex) && !isContentProducerGroup && (
        <Tabs
          className="m-b-30"
          value={activeTabIndex}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="MINE" />
          <Tab label="SHARED WITH ME" />
          <Tab label="FLAMINGO" />
        </Tabs>
      )}

      <CopyLessonDialog
        isOpen={isCopyLessonDialogOpen}
        handleClose={() => toggleCopyLessonDialog(false)}
        lesson={activeLesson}
        setActiveTabIndex={setActiveTabIndex}
      />

      <LessonMenu
        anchorEl={anchorEl}
        isMenuOpen={isMenuOpen}
        toggleMenu={toggleMenu}
        lesson={activeLesson}
        toggleCopyLessonDialog={toggleCopyLessonDialog}
        isEditVisible
      />

      {isLoading && (
        <div className={baseStyles['progress-container']}>
          <CircularProgress />
        </div>
      )}

      {!isLoading && lessons.length === 0 && (
        <div className={styles.empty}>
          <img src={ImageNoData} alt="No Lessons" className={'m-t-30 m-b-30'} />
          {activeTabIndex === 0 && (
            <Button className={baseStyles['btn-pill']} variant="outlined" onClick={handleClickNewLesson}>
              NEW LESSON
            </Button>
          )}
        </div>
      )}

      {!isLoading && lessons?.length > 0 && (
        <div style={{height: 'calc(100vh - 195px)', width: '100%'}}>
          <div style={{display: 'flex', height: '100%'}}>
            <div style={{flexGrow: 1}}>
              {/* The remove prop is not a DataGrid / Grid column property */}
              <DataGridPro
                pagination
                components={{Toolbar: DataGridQuickSearchToolbar}}
                componentsProps={{
                  toolbar: {
                    value: searchText,
                    onChange: (event) => requestSearch(event.target.value),
                    clearSearch: () => requestSearch(''),
                    setFilterButtonEl: setDataGridToolbarFilterButtonEl,
                  },
                  panel: {
                    anchorEl: dataGridToolbarFilterButtonEl,
                  },
                }}
                rows={lessonsClone}
                columns={[
                  {
                    field: 'action',
                    type: 'actions',
                    hideable: false,
                    getActions: (params) => [
                      <GridActionsCellItem
                        icon={<FontAwesomeIcon icon={faPlay} className="clr-dark-blue" />}
                        onClick={(event) => handleClickPreviewLesson(event, params.row)}
                        label="Preview"
                      />,
                      <GridActionsCellItem
                        icon={<FontAwesomeIcon icon={faAlignLeft} className="clr-dark-blue" />}
                        onClick={(event) => handleClickPreviewInstructions(event, params.row)}
                        label="Instructions"
                      />,
                      <GridActionsCellItem
                        icon={<FontAwesomeIcon icon={faEllipsisHAlt} className="clr-dark-blue" />}
                        onClick={(event) => handleClickMenu(event, params.row)}
                        label="Menu"
                      />,
                    ],
                  },
                  {field: 'name', headerName: 'Lesson', width: 150},
                  {field: 'description', headerName: 'Description', width: 250},
                  {
                    field: 'createdAt',
                    headerName: 'Created',
                    width: 150,
                    type: 'dateTime',
                    valueFormatter: (params) => moment(params.value).format('M/D/YYYY h:mm a'),
                  },
                  {
                    field: 'by',
                    headerName: 'By',
                    remove: activeTabIndex === 0,
                    width: 150,
                    renderCell: (params) => {
                      const lessonUser = users[params.row.userID];
                      let imageURL;
                      let name;

                      if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
                        name = `${lessonUser?.firstName ?? ''} ${lessonUser?.lastName ?? ''}`.trim();
                        imageURL = lessonUser?.avatarURL;
                      } else if (activeTabIndex === 2) {
                        const lessonUserGroup = userGroups[params.row.userGroupID];
                        name = lessonUserGroup.name;
                        imageURL = lessonUserGroup?.logoURL;
                      }
                      const initial = name ? name.charAt(0) : null;

                      return (
                        <>
                          <Avatar src={imageURL} style={{display: 'inline-flex', width: 24, height: 24}}>
                            {initial ?? <FontAwesomeIcon icon={faUser} size="xs" />}
                          </Avatar>
                          <span style={{marginLeft: 3, verticalAlign: 'top'}}>{name}</span>
                        </>
                      );
                    },
                  },
                  {
                    field: 'tags',
                    headerName: 'Tags',
                    width: 300,
                    valueGetter: (params) => params.row.tags.map((tag) => tag.name).join(','),
                    renderCell: (params) => {
                      const {tags} = params.row;
                      return (
                        <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
                          {tags.map((tag) => (
                            <Chip key={tag._id} label={tag.name} />
                          ))}
                        </Stack>
                      );
                    },
                  },
                  {
                    field: 'visibility',
                    headerName: 'Visibility',
                    remove: activeTabIndex === 2,
                    valueGetter: (params) =>
                      params.row.visibility === 'USER_GROUP' ? 'Everyone' : params.row.visibility,
                    renderCell: (params) => <VisibilityChip visibility={params.row.visibility} />,
                  },
                ].filter((column) => !column.remove)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
